import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Check, Sparkles } from "lucide-react";
import { LOGO_URL, BRAND_ORANGE, PRACTICE_EMAIL } from "../lib/brand";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  doctor_name: "", practice: "", address_line1: "", address_line2: "",
  city: "", state: "", zip_code: "", phone: "", email: "",
  quantity: 1, notes: "",
};

export default function PadRequestModal({ open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // When opened by a logged-in user, prefill from their profile + last pad request
  useEffect(() => {
    if (!open || !user) {
      setPrefilled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API}/me/pad-request-prefill`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          doctor_name: data.doctor_name || user.name || "",
          practice: data.practice || user.practice || "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          phone: data.phone || "",
          email: data.email || user.email || "",
          quantity: data.quantity || 1,
        }));
        const hasShipping = !!(data.address_line1 && data.city);
        setPrefilled(hasShipping);
      } catch {
        // Fallback to user profile fields only
        setForm((f) => ({
          ...f,
          doctor_name: user.name || "",
          practice: user.practice || "",
          email: user.email || "",
        }));
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  if (!open) return null;
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/public/request-pads`,
        { ...form, quantity: parseInt(form.quantity, 10) || 1 },
        { headers },
      );
      setDone(true);
      toast.success("Request received — we'll ship pads soon");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { setDone(false); setForm(EMPTY_FORM); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50" onClick={close}>
      <div className="my-8 w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 flex items-center gap-4" style={{ backgroundColor: BRAND_ORANGE }}>
          <img src={LOGO_URL} alt="GCOMS" className="w-12 h-12 rounded-full bg-black p-1" />
          <div className="flex-1 text-white">
            <div className="text-[10px] tracking-widest uppercase opacity-90">Restock Request</div>
            <div className="font-bold text-lg">Request Referral Pads</div>
          </div>
          <button data-testid="pads-close" onClick={close} className="text-white/90 hover:text-white"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-white" style={{ backgroundColor: BRAND_ORANGE }}><Check size={28} /></div>
            <h3 className="font-heading text-xl font-semibold mt-4" data-testid="pads-success">Request received</h3>
            <p className="text-sm text-muted-foreground mt-2">We've emailed {PRACTICE_EMAIL} and will ship your pads within 3–5 business days.</p>
            <button onClick={close} className="mt-5 px-5 py-2 rounded-md text-white font-medium" style={{ backgroundColor: BRAND_ORANGE }}>Done</button>
          </div>
        ) : (
        <form onSubmit={submit} className="p-6 space-y-3 max-h-[80vh] overflow-y-auto">
          {prefilled && (
            <div data-testid="pads-prefill-notice" className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#FFF7F0] border border-[#FDE6D8] text-xs">
              <Sparkles size={13} style={{ color: BRAND_ORANGE }} />
              <span className="text-ink/80">We filled this in from your last request — just confirm and submit.</span>
            </div>
          )}
          <p className="text-sm text-muted-foreground">Tell us where to ship your new pads.</p>
          <Field testid="pads-doctor" label="Doctor name *" required v={form.doctor_name} on={(v) => update("doctor_name", v)} />
          <Field testid="pads-practice" label="Practice name *" required v={form.practice} on={(v) => update("practice", v)} />
          <Field testid="pads-addr1" label="Address line 1 *" required v={form.address_line1} on={(v) => update("address_line1", v)} />
          <Field testid="pads-addr2" label="Address line 2" v={form.address_line2} on={(v) => update("address_line2", v)} />
          <div className="grid grid-cols-3 gap-3">
            <Field testid="pads-city" label="City *" required v={form.city} on={(v) => update("city", v)} />
            <Field testid="pads-state" label="State *" required v={form.state} on={(v) => update("state", v)} />
            <Field testid="pads-zip" label="ZIP *" required v={form.zip_code} on={(v) => update("zip_code", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field testid="pads-phone" label="Phone *" required v={form.phone} on={(v) => update("phone", v)} />
            <Field testid="pads-email" label="Email *" type="email" required v={form.email} on={(v) => update("email", v)} />
          </div>
          <Field testid="pads-quantity" label="Quantity (pads)" type="number" v={form.quantity} on={(v) => update("quantity", v)} />
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-medium text-gray-600">Notes</span>
            <textarea data-testid="pads-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm" />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={close} className="px-5 py-2 rounded-md border border-gray-200 text-sm">Cancel</button>
            <button data-testid="pads-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-white font-medium disabled:opacity-60" style={{ backgroundColor: BRAND_ORANGE }}>
              <Send size={16} /> {submitting ? "Sending..." : "Request pads"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", required, v, on }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-medium text-gray-600">{label}</span>
      <input data-testid={testid} type={type} required={required} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24]" />
    </label>
  );
}
